import { useState, useEffect, useRef } from "react";
import { FileText, Upload, Download, Trash2, FileLock2, Loader2, CheckCircle2, XCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const fmtSize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

export const DocumentsPanel = ({ clientId = null, isAdmin = false }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const fileRef = useRef();
  const [dialog, setDialog] = useState(null); // { doc, mode: 'approve' | 'decline' }
  const [signName, setSignName] = useState("");
  const [declineReason, setDeclineReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = clientId ? { client_id: clientId } : {};
      const { data } = await api.get("/documents", { params });
      setDocs(data);
    } catch { toast.error("Failed to load documents"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (clientId) fd.append("client_id", clientId);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded securely");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (doc) => {
    setBusyId(doc.id);
    try {
      const res = await fetch(`${API}/documents/${doc.id}/download`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    finally { setBusyId(null); }
  };

  const remove = async (doc) => {
    setBusyId(doc.id);
    try { await api.delete(`/documents/${doc.id}`); toast.success("Document removed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
    finally { setBusyId(null); }
  };

  const openDialog = (doc, mode) => { setSignName(""); setDeclineReason(""); setDialog({ doc, mode }); };

  const submitApproval = async () => {
    if (!dialog) return;
    if (dialog.mode === "approve" && !signName.trim()) { toast.error("Please type your full name to approve"); return; }
    setSubmitting(true);
    try {
      await api.post(`/documents/${dialog.doc.id}/approval`, {
        action: dialog.mode,
        full_name: signName,
        reason: declineReason,
      });
      toast.success(dialog.mode === "approve" ? "Document approved" : "Document declined");
      setDialog(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
    finally { setSubmitting(false); }
  };

  const uploaderLabel = (doc) => {
    if (doc.uploaded_by_role === "admin") return "Tactis";
    return isAdmin ? "Client" : "You";
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-zinc-400 text-sm"><FileLock2 size={16} /> {docs.length} confidential document{docs.length !== 1 ? "s" : ""}</div>
        <label className="btn-metal px-5 py-2.5 rounded-lg text-sm font-medium cursor-pointer flex items-center gap-2" data-testid="upload-document-btn">
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {uploading ? "Uploading…" : "Upload"}
          <input ref={fileRef} type="file" className="hidden" onChange={onUpload} data-testid="upload-document-input" disabled={uploading} />
        </label>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm py-10 text-center">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/12 py-16 text-center text-zinc-500" data-testid="documents-empty">
          <FileText size={28} strokeWidth={1} className="mx-auto mb-3 opacity-60" />
          No documents yet. Upload confidential files securely.
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 overflow-hidden divide-y divide-white/8" data-testid="documents-list">
          {docs.map((d) => {
            const st = d.approval_status;
            const canAct = !isAdmin && d.uploaded_by_role === "admin" && st === "pending";
            return (
            <div key={d.id} className="flex items-center gap-4 px-5 py-4 bg-[#0b0b0b] hover:bg-[#101010] transition-colors flex-wrap" data-testid={`document-row-${d.id}`}>
              <FileText size={20} strokeWidth={1.3} className="text-zinc-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-white text-sm truncate">{d.original_filename}</div>
                <div className="text-zinc-500 text-xs mt-0.5">
                  {fmtSize(d.size)} · {fmtDate(d.created_at)} · {uploaderLabel(d)}
                </div>
                {st === "approved" && (
                  <div className="flex items-center gap-1.5 text-emerald-400 text-xs mt-1.5" data-testid={`doc-status-${d.id}`}>
                    <CheckCircle2 size={13} /> Approved by {d.approval_name} · {fmtDate(d.approval_at)}
                  </div>
                )}
                {st === "declined" && (
                  <div className="flex items-center gap-1.5 text-red-400 text-xs mt-1.5" data-testid={`doc-status-${d.id}`}>
                    <XCircle size={13} /> Declined · {fmtDate(d.approval_at)}{d.decline_reason ? ` — ${d.decline_reason}` : ""}
                  </div>
                )}
                {st === "pending" && (
                  <div className="flex items-center gap-1.5 text-amber-400 text-xs mt-1.5" data-testid={`doc-status-${d.id}`}>
                    <Lock size={12} /> {isAdmin ? "Awaiting client approval" : "Awaiting your approval"}
                  </div>
                )}
              </div>

              {canAct && (
                <div className="flex items-center gap-2">
                  <button onClick={() => openDialog(d, "approve")} className="px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors text-xs flex items-center gap-1.5" data-testid={`approve-doc-${d.id}`}>
                    <CheckCircle2 size={14} /> Approve
                  </button>
                  <button onClick={() => openDialog(d, "decline")} className="px-3 py-1.5 rounded-lg border border-white/15 text-zinc-300 hover:border-red-500/40 hover:text-red-300 transition-colors text-xs flex items-center gap-1.5" data-testid={`decline-doc-${d.id}`}>
                    <XCircle size={14} /> Decline
                  </button>
                </div>
              )}

              <button onClick={() => download(d)} disabled={busyId === d.id} className="p-2 text-zinc-400 hover:text-white transition-colors" data-testid={`download-doc-${d.id}`}>
                {busyId === d.id ? <Loader2 size={17} className="animate-spin" /> : <Download size={17} />}
              </button>
              {st === "approved" ? (
                <span className="p-2 text-emerald-500/70" title="Approved and locked" data-testid={`locked-doc-${d.id}`}><Lock size={16} /></span>
              ) : (
                <button onClick={() => remove(d)} disabled={busyId === d.id} className="p-2 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`delete-doc-${d.id}`}>
                  <Trash2 size={17} />
                </button>
              )}
            </div>
          );})}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="bg-[#0d0d0d] border-white/12 text-white" data-testid="approval-dialog">
          <DialogHeader>
            <DialogTitle className="font-dash">
              {dialog?.mode === "approve" ? "Approve document" : "Decline document"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-zinc-500 text-sm -mt-2 truncate">{dialog?.doc?.original_filename}</p>
          {dialog?.mode === "approve" ? (
            <div className="space-y-3">
              <p className="text-zinc-400 text-sm">By typing your full name below you confirm your digital approval of this document. This will be recorded with a timestamp and the document will be locked.</p>
              <div>
                <Label className="text-xs text-zinc-500">Full name (digital signature)</Label>
                <Input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="e.g. Alexandra Whitfield"
                  className="mt-2 bg-[#0b0b0b] border-white/12 text-white" data-testid="approval-name-input" />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-zinc-400 text-sm">Please provide a brief reason for declining (optional but recommended).</p>
              <Textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} rows={3} placeholder="Reason for declining…"
                className="bg-[#0b0b0b] border-white/12 text-white" data-testid="decline-reason-input" />
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setDialog(null)} className="px-4 py-2.5 rounded-lg border border-white/15 text-zinc-300 hover:text-white text-sm" data-testid="approval-cancel">Cancel</button>
            <button onClick={submitApproval} disabled={submitting} className="btn-metal px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-60" data-testid="approval-confirm">
              {submitting ? "Submitting…" : dialog?.mode === "approve" ? "Confirm approval" : "Confirm decline"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
